import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ComponentProps,
} from "react";
import {
  Content,
  FormContentGap,
  FormPanelShadow,
  Hero,
  MainContentContainer,
  PageContainer,
  StickyHeroBlock,
  StickyPanel,
  StickyPanelInner,
  Tab,
  Tabs,
  WhiteCard,
} from "./Styles";
import { COMMON_LANDING_CONSTANTS } from "./constants";
import type {
  CommonLandingMwebData,
  TravelInsuranceTab,
  TravelProtectionTab,
} from "./CommonLandingMweb.types";
import {
  BulkBookingPersuasion,
  DropOffBottomSheet,
  HeroBar,
  PoweredBy,
  RecentSearches,
  StickyHeader,
  TravelInsuranceCard,
  TravelPlans,
  TravelProtectionTooltip,
  TripProtectionCarousel,
} from "./components";
import LandingFaq from "../LandingFaq/LandingFaq";
import FormattedText from "../FormattedText/FormattedText";
import type { RecentSearchItem } from "./components/RecentSearches/RecentSearches";
import {
  buildRecentSearchNextPageRequest,
  navigateRecentSearchNextPage,
} from "../../utils/recentSearchNavigation";

type TabEntry = TravelInsuranceTab | TravelProtectionTab;

interface CommonLandingMwebProps {
  data?: CommonLandingMwebData;
}

function useStickyOnScroll(threshold: number) {
  const [showSticky, setShowSticky] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > threshold);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return showSticky;
}

const CommonLandingMweb = ({ data }: CommonLandingMwebProps) => {
  const [selectedTab, setSelectedTab] = useState<string>("");
  const [showTooltip, setShowTooltip] = useState(false);
  const [showDropOffBottomSheet, setShowDropOffBottomSheet] = useState(false);
  const [countryDisplayText, setCountryDisplayText] = useState<string>(
    COMMON_LANDING_CONSTANTS.DEFAULT_SUBTITLE,
  );
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [travellerCount, setTravellerCount] = useState<number | undefined>(undefined);
  const [selectedPlanType, setSelectedPlanType] = useState<string | undefined>(undefined);

  const showSticky = useStickyOnScroll(
    COMMON_LANDING_CONSTANTS.STICKY_HEADER_SCROLL_THRESHOLD,
  );

  useEffect(() => {
    const tabs = data?.travelPlanTabs;
    setSelectedTab(
      (tabs as TabEntry[] | undefined)?.find((tab) => "isSelected" in tab && tab.isSelected)?.id ||
        tabs?.[0]?.id ||
        COMMON_LANDING_CONSTANTS.TRAVEL_INSURANCE_TAB_ID,
    );
  }, [data?.travelPlanTabs]);

  useEffect(() => {
    if (data?.itineraryId) {
      sessionStorage.setItem("itineraryId", data.itineraryId);
    }
  }, [data?.itineraryId]);

  const selectedTabData = useMemo(
    () => data?.travelPlanTabs?.find((tab) => tab.id === selectedTab),
    [data?.travelPlanTabs, selectedTab],
  );

  const isTravelInsurance =
    selectedTab === COMMON_LANDING_CONSTANTS.TRAVEL_INSURANCE_TAB_ID;

  const promoText = data?.heading?.subtitle;
  const hasHeadingPersuasion = Boolean(
    data?.headingPersuasion &&
      (data.headingPersuasion.stats != null || data.headingPersuasion.icon),
  );

  const heroBarProps = {
    title: data?.heading?.title,
    headingPersuasion: data?.headingPersuasion,
    promoText,
    hasHeadingPersuasion,
  };

  const handleRecentSearchPress = useCallback(
    async (item: RecentSearchItem) => {
      try {
        await navigateRecentSearchNextPage(
          buildRecentSearchNextPageRequest(item),
        );
      } catch (err) {
        console.error("Recent search navigation failed", err);
      }
    },
    [],
  );

  const handleCountryChange = useCallback(
    (countries: Array<{ name?: string }> | undefined) => {
      const names = countries?.map((c) => c?.name).filter(Boolean);
      setCountryDisplayText(
        names?.length
          ? names.join(", ")
          : COMMON_LANDING_CONSTANTS.DEFAULT_SUBTITLE,
      );
    },
    [],
  );

  const handleDateChange = useCallback((start?: Date, end?: Date) => {
    setFromDate(start);
    setToDate(end);
  }, []);

  const handleSegmentChange = useCallback(
    (segments: Array<{ isSelected?: boolean; name?: string }> | undefined) => {
      setSelectedPlanType(segments?.find((s) => s?.isSelected)?.name);
    },
    [],
  );

  const travelPlanTabs = data?.travelPlanTabs || [];

  return (
    <PageContainer>
      <Hero>
        <HeroBar {...heroBarProps} />
      </Hero>

      <Content>
        <FormPanelShadow $pullUp={hasHeadingPersuasion}>
          <WhiteCard>
            {travelPlanTabs.length >= 2 ? (
              <Tabs>
                {travelPlanTabs.slice(0, 2).map((tab) => (
                  <Tab
                    key={tab.id}
                    $active={selectedTab === tab.id}
                    onClick={() => setSelectedTab(tab.id)}
                  >
                    <FormattedText value={tab.title} />
                  </Tab>
                ))}
              </Tabs>
            ) : null}
            <FormContentGap>
              {isTravelInsurance ? (
                <TravelInsuranceCard
                  travelInsuranceData={selectedTabData as TravelInsuranceTab}
                  onCountryChange={handleCountryChange}
                  onDateChange={handleDateChange}
                  onTravellerChange={setTravellerCount}
                  onSegmentChange={handleSegmentChange}
                  onCTAPress={() => void 0}
                />
              ) : (
                <TravelPlans
                  title={(selectedTabData as TravelProtectionTab)?.title}
                  plans={(selectedTabData as TravelProtectionTab)?.plans}
                  isVisible
                />
              )}
            </FormContentGap>
          </WhiteCard>
        </FormPanelShadow>

        <MainContentContainer>
          <BulkBookingPersuasion data={data?.bulkBookingPersuasion} />
          <RecentSearches
            title={data?.recentSearches?.title}
            recentSearches={data?.recentSearches?.data as RecentSearchItem[] | undefined}
            onSearchPress={handleRecentSearchPress}
          />
          <TripProtectionCarousel
            title={data?.carouselCards?.title}
            cards={data?.carouselCards?.cards}
          />
          <PoweredBy
            title={data?.poweredBy?.title}
            icon={data?.poweredBy?.icon}
            iconsList={data?.poweredBy?.iconsList}
            description={data?.poweredBy?.descrption}
          />
          {data?.faq?.faqList?.length ? (
            <LandingFaq
              customTitle={data.faq.title}
              faq={data.faq.faqList as ComponentProps<typeof LandingFaq>["faq"]}
              customInfo={data.faq.info}
              icon={data.faq.icon}
              isCardView
            />
          ) : null}
        </MainContentContainer>
      </Content>

      <StickyPanel $show={showSticky} aria-hidden={!showSticky}>
        <StickyHeroBlock>
          <HeroBar {...heroBarProps} />
        </StickyHeroBlock>
        <StickyPanelInner>
          <StickyHeader
            heading={data?.heading}
            selectedCountry={countryDisplayText}
            ctaLabel={(selectedTabData as any)?.onScrollData?.ctaText}
            editLabel={(selectedTabData as any)?.onScrollData?.editLabel}
            fromDate={fromDate}
            toDate={toDate}
            travellerCount={travellerCount}
            selectedPlanType={selectedPlanType}
            onExplorePlansPress={() => void 0}
          />
        </StickyPanelInner>
      </StickyPanel>

      <TravelProtectionTooltip
        visible={showTooltip}
        tooltipData={data?.toolTip}
        onClose={() => setShowTooltip(false)}
      />

      <DropOffBottomSheet
        visible={showDropOffBottomSheet}
        setVisible={setShowDropOffBottomSheet}
        dropOffData={data?.dropOffBottomSheet}
      />
    </PageContainer>
  );
};

export default CommonLandingMweb;
